// src/components/admin/MarketplaceManagement.tsx
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Card, CardContent, Paper, useTheme, alpha, Snackbar, Alert, FormControl,
  InputLabel, Select, MenuItem, TextField, InputAdornment, SelectChangeEvent,
  Tabs, Tab, CircularProgress, Tooltip, Divider, List, ListItem, ListItemText
} from '@mui/material';
import {
  CheckCircle as ApproveIcon, Cancel as RejectIcon, Visibility as ViewIcon,
  Search as SearchIcon, VisibilityOff as HideIcon, Delete as DeleteIcon,
  Storefront as ProjectIcon, AccountBalance as InvestorIcon,
  Pending as PendingIcon, Block as BlockedIcon, Launch as LaunchIcon,
  ArticleOutlined as DocumentIcon, CheckCircleOutline as CheckIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import * as marketplaceApi from '../../services/marketplace';

const MotionCard = motion(Card);

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'info' | 'warning' | 'error';
}

const MarketplaceManagement: React.FC = () => {
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [projects, setProjects] = useState<marketplaceApi.MarketplaceProject[]>([]);
  const [investorRequests, setInvestorRequests] = useState<marketplaceApi.InvestorRequest[]>([]);
  const [selectedItem, setSelectedItem] = useState<marketplaceApi.MarketplaceProject | marketplaceApi.InvestorRequest | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [projectsData, requestsData] = await Promise.all([
        marketplaceApi.getAllProjectsAdmin(),
        marketplaceApi.getAllInvestorRequestsAdmin()
      ]);
      setProjects(projectsData);
      setInvestorRequests(requestsData);
    } catch (error) {
      showSnackbar('Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: SnackbarState['severity']) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleApprove = async (id: string, type: 'project' | 'investor') => {
    try {
      if (type === 'project') {
        await marketplaceApi.approveProject(id);
      } else {
        await marketplaceApi.approveInvestorRequest(id);
      }
      showSnackbar(`${type === 'project' ? 'Project' : 'Request'} approved successfully`, 'success');
      loadData();
    } catch (error) {
      showSnackbar('Error approving item', 'error');
    }
  };

  const handleReject = async () => {
    if (!selectedItem) return;
    try {
      const isProject = 'country' in selectedItem;
      if (isProject) {
        await marketplaceApi.rejectProject(selectedItem.id, rejectionReason);
      } else {
        await marketplaceApi.rejectInvestorRequest(selectedItem.id, rejectionReason);
      }
      showSnackbar(`${isProject ? 'Project' : 'Request'} rejected`, 'success');
      setRejectDialogOpen(false);
      setRejectionReason('');
      loadData();
    } catch (error) {
      showSnackbar('Error rejecting item', 'error');
    }
  };

  const handleHide = async (id: string, type: 'project' | 'investor') => {
    try {
      if (type === 'project') {
        await marketplaceApi.hideProject(id);
      } else {
        await marketplaceApi.hideInvestorRequest(id);
      }
      showSnackbar('Item hidden successfully', 'success');
      loadData();
    } catch (error) {
      showSnackbar('Error hiding item', 'error');
    }
  };

  const handleDelete = async (id: string, type: 'project' | 'investor') => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      if (type === 'project') {
        await marketplaceApi.deleteProjectAdmin(id);
      } else {
        await marketplaceApi.deleteInvestorRequestAdmin(id);
      }
      showSnackbar('Item deleted successfully', 'success');
      loadData();
    } catch (error) {
      showSnackbar('Error deleting item', 'error');
    }
  };

  const filteredProjects = projects.filter(p => {
    const matchesSearch = p.projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.organization.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || p.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const filteredRequests = investorRequests.filter(r => {
    const matchesSearch = r.projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         r.organization.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || r.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      case 'hidden': return 'default';
      default: return 'default';
    }
  };

  const stats = [
    {
      title: 'Total Projects',
      value: projects.length,
      pending: projects.filter(p => p.status === 'pending').length,
      icon: <ProjectIcon />,
      color: 'primary' as const
    },
    {
      title: 'Investor Requests',
      value: investorRequests.length,
      pending: investorRequests.filter(r => r.status === 'pending').length,
      icon: <InvestorIcon />,
      color: 'secondary' as const
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
        Marketplace Management
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} md={6} key={index}>
            <MotionCard
              whileHover={{ y: -4 }}
              elevation={2}
              sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette[stat.color].main, 0.1)}, ${alpha(theme.palette[stat.color].light, 0.05)})`,
                border: `1px solid ${alpha(theme.palette[stat.color].main, 0.1)}`
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">{stat.title}</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, mt: 1 }}>{stat.value}</Typography>
                    <Chip
                      label={`${stat.pending} Pending`}
                      size="small"
                      color="warning"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(theme.palette[stat.color].main, 0.1),
                      color: theme.palette[stat.color].main
                    }}
                  >
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </MotionCard>
          </Grid>
        ))}
      </Grid>

      {/* Tabs and Content */}
      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Projects" icon={<ProjectIcon />} iconPosition="start" />
          <Tab label="Investor Requests" icon={<InvestorIcon />} iconPosition="start" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name or organization..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="hidden">Hidden</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Organization</TableCell>
                    <TableCell>Sector</TableCell>
                    {tab === 0 && <TableCell>Country</TableCell>}
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(tab === 0 ? filteredProjects : filteredRequests).map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.projectName}</TableCell>
                      <TableCell>{item.organization}</TableCell>
                      <TableCell>{item.sector}</TableCell>
                      {tab === 0 && <TableCell>{'country' in item ? item.country : '-'}</TableCell>}
                      <TableCell>
                        <Chip
                          label={item.status}
                          color={getStatusColor(item.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(item.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedItem(item);
                              setDetailsDialogOpen(true);
                            }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {item.status === 'pending' && (
                          <>
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleApprove(item.id, tab === 0 ? 'project' : 'investor')}
                              >
                                <ApproveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setSelectedItem(item);
                                  setRejectDialogOpen(true);
                                }}
                              >
                                <RejectIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {item.status !== 'hidden' && (
                          <Tooltip title="Hide">
                            <IconButton
                              size="small"
                              onClick={() => handleHide(item.id, tab === 0 ? 'project' : 'investor')}
                            >
                              <HideIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(item.id, tab === 0 ? 'project' : 'investor')}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {selectedItem && (
            <Box>
              <Typography variant="h5" fontWeight={600}>{selectedItem.projectName}</Typography>
              <Chip
                label={selectedItem.status}
                color={getStatusColor(selectedItem.status)}
                size="small"
                sx={{ mt: 1 }}
              />
            </Box>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '70vh' }}>
          {selectedItem && (
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Organization</Typography>
                <Typography>{selectedItem.organization}</Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Sector</Typography>
                <Typography>{selectedItem.sector}</Typography>
              </Grid>

              {'country' in selectedItem && (
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Country</Typography>
                  <Typography>{selectedItem.country}</Typography>
                </Grid>
              )}

              {'stage' in selectedItem && (
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Stage</Typography>
                  <Typography>{selectedItem.stage}</Typography>
                </Grid>
              )}

              {'owner' in selectedItem && (
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Project Owner</Typography>
                  <Typography>{selectedItem.owner}</Typography>
                </Grid>
              )}

              {'investor' in selectedItem && (
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Investor</Typography>
                  <Typography>{selectedItem.investor}</Typography>
                </Grid>
              )}

              {'location' in selectedItem && selectedItem.location && (
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Preferred Location</Typography>
                  <Typography>{selectedItem.location}</Typography>
                </Grid>
              )}

              {'investmentType' in selectedItem && selectedItem.investmentType && (
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Investment Type</Typography>
                  <Typography>{selectedItem.investmentType}</Typography>
                </Grid>
              )}

              {/* Summary/Goal */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                  {'summary' in selectedItem ? 'Project Summary' : 'Investment Goal'}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" paragraph>
                  {'summary' in selectedItem ? selectedItem.summary : selectedItem.goal}
                </Typography>
              </Grid>

              {/* Key Activities (Projects only) */}
              {'keyActivities' in selectedItem && selectedItem.keyActivities && selectedItem.keyActivities.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Key Activities
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <List dense>
                    {selectedItem.keyActivities.map((item, index) => (
                      <ListItem key={index} sx={{ pl: 0 }}>
                        <CheckIcon sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                        <ListItemText primary={item.activity} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              )}

              {/* Product Description (Projects only) */}
              {'productDescription' in selectedItem && selectedItem.productDescription && (
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Product/Service Description
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.primary.light, 0.1),
                    borderRadius: 2,
                    borderLeft: `4px solid ${theme.palette.primary.main}`
                  }}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {selectedItem.productDescription}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* Competitive Advantages (Projects only) */}
              {'advantages' in selectedItem && selectedItem.advantages && (
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Competitive Advantages
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.primary.light, 0.1),
                    borderRadius: 2,
                    borderLeft: `4px solid ${theme.palette.primary.main}`
                  }}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {selectedItem.advantages}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* Preferred Industry (Investor Requests only) */}
              {'preferredIndustry' in selectedItem && selectedItem.preferredIndustry && (
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Preferred Industry Focus
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body1" paragraph>
                    {selectedItem.preferredIndustry}
                  </Typography>
                </Grid>
              )}

              {/* Proposal Requirements (Investor Requests only) */}
              {'proposal' in selectedItem && selectedItem.proposal && (
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Proposal Requirements
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.secondary.light, 0.1),
                    borderRadius: 2,
                    borderLeft: `4px solid ${theme.palette.secondary.main}`
                  }}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {selectedItem.proposal}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* ROI/Investment Returns (Investor Requests only) */}
              {'roi' in selectedItem && selectedItem.roi && (
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Investment Returns & Expectations
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.secondary.light, 0.1),
                    borderRadius: 2,
                    borderLeft: `4px solid ${theme.palette.secondary.main}`
                  }}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {selectedItem.roi}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* External Links */}
              {selectedItem.links && selectedItem.links.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    External Links
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box>
                    {selectedItem.links.map((link, index) => (
                      <Button
                        key={index}
                        variant="outlined"
                        color="primary"
                        endIcon={<LaunchIcon />}
                        sx={{ mb: 1, mr: 2 }}
                        onClick={() => window.open(link.url, '_blank')}
                      >
                        {link.title || link.url}
                      </Button>
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Documents */}
              {selectedItem.documents && selectedItem.documents.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Available Documents
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box>
                    {selectedItem.documents.map((doc, index) => (
                      <Chip
                        key={index}
                        icon={<DocumentIcon />}
                        label={doc.name}
                        variant="outlined"
                        sx={{ mr: 1, mb: 1 }}
                        onClick={() => window.open(doc.url, '_blank')}
                      />
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Next Steps */}
              {selectedItem.nextSteps && (
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Next Steps
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.info.light, 0.1),
                    borderRadius: 2,
                    borderLeft: `4px solid ${theme.palette.info.main}`
                  }}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {selectedItem.nextSteps}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* Rejection Reason */}
              {selectedItem.rejectionReason && (
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Rejection Reason
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.error.light, 0.1),
                    borderRadius: 2,
                    borderLeft: `4px solid ${theme.palette.error.main}`
                  }}>
                    <Typography color="error">{selectedItem.rejectionReason}</Typography>
                  </Box>
                </Grid>
              )}

              {/* Timestamps */}
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                  Submission Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Created At</Typography>
                <Typography>{new Date(selectedItem.createdAt).toLocaleString()}</Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Last Updated</Typography>
                <Typography>{new Date(selectedItem.updatedAt).toLocaleString()}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Item</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason (optional)"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReject} color="error" variant="contained">
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MarketplaceManagement;
